import getItem from "@helpers/items/getItem";
import { getProductPrice } from "@helpers/products/getProductPrice";
import { optionsToVariantName } from "@helpers/products/optionsToVariantName";

import type { Item } from "@typings/product/item";
import type { Product } from "@typings/product/product";

const MAX_SHARED_CART_PARAM_LENGTH = 1500;

export const sharedCartParamKey = "share";

type EncodedCartItem = {
  itemId: number;
  quantity: number;
  offerId: number;
  options: Record<number, number>;
  variant?: string;
  isFbw?: boolean;
  upsellOrderId?: number;
};

const sanitizeOptions = (item: Item, options: Record<number, number> = {}) => {
  return item.options.reduce<Record<number, number>>((acc, option) => {
    const providedValueId = options[option.id];
    const matchingValue = option.values.find((value) => value.id === Number(providedValueId));

    if (matchingValue) {
      acc[option.id] = matchingValue.id;

      return acc;
    }

    if (option.values.length) {
      acc[option.id] = option.values[0].id;
    }

    return acc;
  }, {});
};

const isGift = (product: Product) => product.item.category.name === "Free Gift";

export const encodeSharedCartParam = (cartItems: Product[]): string | null => {
  const encodedItems: EncodedCartItem[] = cartItems
    .filter((product) => !isGift(product))
    .map((product) => ({
      itemId: product.item.id,
      quantity: product.configuration.quantity,
      offerId: product.configuration.offer.id,
      options: product.configuration.options,
      variant: optionsToVariantName(product.item.options, product.configuration.options),
      isFbw: product.configuration.isFbw || undefined,
      upsellOrderId: product.configuration.upsell?.orderId,
    }));

  if (!encodedItems.length) return null;

  const serializedCart = JSON.stringify(encodedItems);
  const encodedCart = encodeURIComponent(serializedCart);

  if (encodedCart.length > MAX_SHARED_CART_PARAM_LENGTH) return null;

  return encodedCart;
};

const buildProductFromEncodedItem = (encodedItem: EncodedCartItem): Product | null => {
  const item = getItem(encodedItem.itemId);

  if (!item || item.category.name === "Free Gift") return null;

  const offer = item.offers.find((o) => o.id === encodedItem.offerId);

  if (!offer) return null;

  const sanitizedOptions = sanitizeOptions(item, encodedItem.options || {});

  const isPromotion = Boolean(encodedItem.isFbw || encodedItem.upsellOrderId);
  const { price, oldPrice } = getProductPrice(item, offer, sanitizedOptions, isPromotion);

  return {
    item,
    configuration: {
      price,
      oldPrice,
      offer,
      options: sanitizedOptions,
      quantity: Math.max(1, Number(encodedItem.quantity) || 1),
      isFbw: encodedItem.isFbw || undefined,
      upsell: encodedItem.upsellOrderId
        ? {
            orderId: encodedItem.upsellOrderId,
          }
        : undefined,
    },
  };
};

export const decodeSharedCartParam = (param: string): Product[] | null => {
  if (!param || param.length > MAX_SHARED_CART_PARAM_LENGTH) return null;

  try {
    const decodedString = decodeURIComponent(param);
    const parsedItems: EncodedCartItem[] = JSON.parse(decodedString);

    if (!Array.isArray(parsedItems)) return null;

    const products = parsedItems
      .map((encodedItem) => buildProductFromEncodedItem(encodedItem))
      .filter((product): product is Product => Boolean(product));

    if (!products.length) return null;

    return products;
  } catch (error) {
    console.warn("Failed to decode shared cart", error);

    return null;
  }
};

export const buildSharedCartUrl = (baseUrl: string, cartItems: Product[], path = "/cart") => {
  const encodedParam = encodeSharedCartParam(cartItems);

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (!encodedParam) {
    return `${baseUrl}${normalizedPath}`;
  }

  const separator = normalizedPath.includes("?") ? "&" : "?";

  return `${baseUrl}${normalizedPath}${separator}${sharedCartParamKey}=${encodedParam}`;
};
